/**
 * Checkout Error Recovery Composable
 *
 * Handles construction of "Go Back" URL when checkout validation fails.
 * Fetches NBC data if productCode is available to get pdpUrl.
 *
 * IMPORTANT: NBC fetch is attempted only once. If the fetch fails,
 * `hasAttemptedFetch` remains true and subsequent button clicks
 * will use browser history.back(). This is intentional - we don't
 * want to retry failed API calls on user interaction.
 */

import { ref, computed } from 'vue';
import { useCheckoutStore } from '@/stores/checkout';
import { getProduct } from '@/services/api/nbc';
import {
  buildCheckoutRedirectUrl,
  navigateToUrlOrBack,
} from '@/utils/checkout/checkoutRedirectHelper';

export function useCheckoutErrorRecovery() {
  const checkout = useCheckoutStore();

  const isLoading = ref(false);
  const pdpUrl = ref<string | null>(null);

  /**
   * Tracks whether NBC fetch has been attempted.
   *
   * Once set to true, no further fetch attempts will be made.
   * This means:
   * - If fetch succeeds: pdpUrl is set, button navigates to PDP
   * - If fetch fails: pdpUrl remains null, button uses browser back
   *
   * This is INTENTIONAL behavior - we don't retry failed API calls.
   */
  const hasAttemptedFetch = ref(false);

  /**
   * Determines if we can potentially build a PDP URL
   * (productCode must be present)
   */
  const canBuildPdpUrl = computed(() => {
    return checkout.hasProductCode && checkout.productCode !== null;
  });

  /**
   * Build the full redirect URL with available valid parameters
   */
  const errorBackUrl = computed(() => {
    if (!pdpUrl.value) return null;

    return buildCheckoutRedirectUrl({
      pdpUrl: pdpUrl.value,
      checkIn: checkout.checkIn,
      checkOut: checkout.checkOut,
      nightCount: checkout.nightCount,
      roomOccupancy: checkout.roomOccupancy,
    });
  });

  /**
   * Fetch NBC data to get pdpUrl
   *
   * Should be called when validation fails but productCode is present.
   *
   * NOTE: This function is idempotent - it will only execute once.
   * After the first call (success or failure), hasAttemptedFetch is set
   * to true and subsequent calls are no-ops. If the NBC call fails,
   * the button will permanently fall back to browser history.back().
   */
  async function fetchPdpUrl(): Promise<void> {
    if (!canBuildPdpUrl.value || !checkout.productCode) {
      return;
    }

    // Prevent multiple fetch attempts - this is intentional
    if (hasAttemptedFetch.value) {
      return;
    }

    hasAttemptedFetch.value = true;
    isLoading.value = true;

    try {
      const result = await getProduct(checkout.productCode);
      pdpUrl.value = result?.pdpUrl ?? null;
    } catch (error) {
      // On failure, pdpUrl remains null → button will use browser back
      console.warn('[ErrorRecovery] Failed to fetch NBC data:', error);
      pdpUrl.value = null;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Navigate back - either to PDP URL or browser history
   */
  function navigateBack(): void {
    navigateToUrlOrBack(errorBackUrl.value);
  }

  return {
    // State
    isLoading,
    pdpUrl,
    errorBackUrl,
    canBuildPdpUrl,
    hasAttemptedFetch,

    // Actions
    fetchPdpUrl,
    navigateBack,
  };
}
